import { useState, useMemo } from "react";
import { Text, ScrollView } from "react-native";
import {
  Snowflake,
  Baby,
  Heart,
  Telescope,
  ChevronLeft,
  Compass,
  Gem,
  HeartHandshake,
  HelpCircle,
  CalendarClock,
  Check,
  Activity,
  Egg,
  Droplets,
} from "lucide-react-native";
import { EMBER } from "../../../../onboarding/theme";
import type { PrimaryIntent } from "../../../../onboarding/types";
import { usePatient } from "@louise/patients/hooks/usePatient";
import {
  ActionBanner,
  Dialog,
  DialogHeader,
  IconButton,
  Row,
  semanticColors,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import type { TranslationFunction } from "@src/packages/i18n";
import type { FertilityIntentStage } from "@src/packages/experience/fertilityStage";

/** The onboarding stage a picker choice records. */
export type IntentPickerStage = Extract<
  FertilityIntentStage,
  "exploring-options" | "trying-naturally" | "getting-ready" | "in-treatment"
>;
export type IntentPickerPrimaryIntent = PrimaryIntent;

type TreatmentGoalValue =
  | "exploring"
  | "egg-preserve"
  | "sperm-preserve"
  | "embryo-preserve"
  | "pregnancy"
  | "egg-donation"
  | "sperm-donation"
  | "surrogacy"
  | "adoption";

type Goal = "preserve" | "grow" | "donate" | "explore";

/** The first picker step is the onboarding path; the API calls it primaryIntent. */
const PRIMARY_INTENT_BY_GOAL: Record<Goal, PrimaryIntent> = {
  preserve: "preserve",
  grow: "grow",
  donate: "donate",
  explore: "learn",
};

type GoalItem = {
  id: Goal;
  label: string;
  sublabel: string;
  Icon: typeof Snowflake;
  directGoal?: TreatmentGoalValue;
  stage?: IntentPickerStage;
};

function getGoals(t: TranslationFunction): GoalItem[] {
  return [
    {
      id: "preserve",
      label: t("you.intent_picker.goal.preserve_label"),
      sublabel: t("you.intent_picker.goal.preserve_sublabel"),
      Icon: Snowflake,
    },
    {
      id: "grow",
      label: t("you.intent_picker.goal.grow_label"),
      sublabel: t("you.intent_picker.goal.grow_sublabel"),
      Icon: Baby,
    },
    {
      id: "donate",
      label: t("you.intent_picker.goal.donate_label"),
      sublabel: t("you.intent_picker.goal.donate_sublabel"),
      Icon: Heart,
    },
    {
      id: "explore",
      label: t("you.intent_picker.goal.explore_label"),
      sublabel: t("you.intent_picker.goal.explore_sublabel"),
      Icon: Telescope,
      directGoal: "exploring",
      stage: "exploring-options",
    },
  ];
}

type StageOption = {
  label: string;
  sublabel?: string;
  goal: TreatmentGoalValue;
  stage: IntentPickerStage;
  Icon: typeof Compass;
};

function getPreserveOptions(
  t: TranslationFunction,
  gender: string | null | undefined,
): StageOption[] {
  const isAMAB = gender === "AMAB";
  return [
    {
      label: t("you.intent_picker.preserve.exploring_label"),
      sublabel: t("you.intent_picker.preserve.exploring_sublabel"),
      goal: "exploring",
      stage: "exploring-options",
      Icon: Compass,
    },
    {
      label: isAMAB
        ? t("you.intent_picker.preserve.sperm_freezing_label")
        : t("you.intent_picker.preserve.egg_freezing_label"),
      sublabel: t("you.intent_picker.preserve.freezing_sublabel"),
      goal: isAMAB ? "sperm-preserve" : "egg-preserve",
      stage: "getting-ready",
      Icon: Snowflake,
    },
    {
      label: t("you.intent_picker.preserve.embryo_label"),
      sublabel: t("you.intent_picker.preserve.embryo_sublabel"),
      goal: "embryo-preserve",
      stage: "getting-ready",
      Icon: Gem,
    },
  ];
}

function getGrowOptions(t: TranslationFunction): StageOption[] {
  return [
    {
      label: t("you.intent_picker.grow.trying_label"),
      sublabel: t("you.intent_picker.grow.trying_sublabel"),
      goal: "pregnancy",
      stage: "trying-naturally",
      Icon: HeartHandshake,
    },
    {
      label: t("you.intent_picker.grow.researching_label"),
      sublabel: t("you.intent_picker.grow.researching_sublabel"),
      goal: "exploring",
      stage: "exploring-options",
      Icon: HelpCircle,
    },
    {
      label: t("you.intent_picker.grow.adoption_label"),
      sublabel: t("you.intent_picker.grow.adoption_sublabel"),
      goal: "adoption",
      stage: "exploring-options",
      Icon: HeartHandshake,
    },
    {
      label: t("you.intent_picker.grow.ready_label"),
      sublabel: t("you.intent_picker.grow.ready_sublabel"),
      goal: "pregnancy",
      stage: "getting-ready",
      Icon: CalendarClock,
    },
    {
      label: t("you.intent_picker.grow.in_treatment_label"),
      sublabel: t("you.intent_picker.grow.in_treatment_sublabel"),
      goal: "pregnancy",
      stage: "in-treatment",
      Icon: Activity,
    },
  ];
}

function getHelpOptions(
  t: TranslationFunction,
  gender: string | null | undefined,
): StageOption[] {
  const isAMAB = gender === "AMAB";
  if (isAMAB) {
    return [
      {
        label: t("you.intent_picker.help.sperm_donation_label"),
        sublabel: t("you.intent_picker.help.sperm_donation_sublabel"),
        goal: "sperm-donation",
        stage: "exploring-options",
        Icon: Droplets,
      },
    ];
  }
  return [
    {
      label: t("you.intent_picker.help.egg_donation_label"),
      sublabel: t("you.intent_picker.help.egg_donation_sublabel"),
      goal: "egg-donation",
      stage: "exploring-options",
      Icon: Egg,
    },
    {
      label: t("you.intent_picker.help.surrogacy_label"),
      sublabel: t("you.intent_picker.help.surrogacy_sublabel"),
      goal: "surrogacy",
      stage: "getting-ready",
      Icon: HeartHandshake,
    },
  ];
}

type Props = {
  visible: boolean;
  currentIntent: string | null;
  genderOverride?: "AFAB" | "AMAB" | null;
  onSelect: (
    goal: string,
    stage: IntentPickerStage | undefined,
    primaryIntent: PrimaryIntent,
  ) => void | boolean | Promise<void | boolean>;
  onClose: () => void;
  isSaving?: boolean;
  saveError?: string | null;
};

function PickerRow({
  Icon,
  label,
  sublabel,
  selected,
  trailing,
  disabled = false,
  onPress,
}: {
  Icon: typeof Snowflake;
  label: string;
  sublabel?: string;
  selected: boolean;
  trailing: "chevron" | "check" | "none";
  disabled?: boolean;
  onPress: () => void;
}) {
  // The canonical Row: icon, title, subtitle, chevron when it leads on, a
  // check on the current choice.
  return (
    <Row
      leading={
        <Icon
          size={20}
          color={semanticColors.text.secondary}
          strokeWidth={1.5}
        />
      }
      leadingSize={24}
      title={label}
      subtitle={sublabel}
      chevron={trailing === "chevron"}
      trailing={
        trailing === "check" ? (
          <Check
            size={18}
            color={semanticColors.text.primary}
            strokeWidth={2.2}
          />
        ) : undefined
      }
      disabled={disabled}
      accessibilityState={{ selected, disabled }}
      // Inside the dialog's scroll area a shadow clips into a grey band.
      elevated={false}
      action="intent_picker_row"
      onPress={onPress}
    />
  );
}

export default function IntentPickerModal({
  visible,
  currentIntent,
  genderOverride = null,
  onSelect,
  onClose,
  isSaving = false,
  saveError = null,
}: Props) {
  const t = useTranslations();
  const { data: patient } = usePatient();
  const [step, setStep] = useState<"goal" | "stage">("goal");
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);

  const goals = useMemo(() => getGoals(t), [t]);
  const pickerGender = genderOverride ?? patient?.gender;
  const preserveOptions = useMemo(
    () => getPreserveOptions(t, pickerGender),
    [pickerGender, t],
  );
  const growOptions = useMemo(() => getGrowOptions(t), [t]);
  const helpOptions = useMemo(
    () => getHelpOptions(t, pickerGender),
    [pickerGender, t],
  );
  const donationGoal =
    helpOptions.length === 1 ? helpOptions[0]?.goal : undefined;

  const resetAfterAnimation = () => {
    setTimeout(() => {
      setStep("goal");
      setSelectedGoal(null);
    }, 300);
  };

  const handleClose = () => {
    onClose();
    resetAfterAnimation();
  };

  const handleGoalSelect = async (goal: Goal) => {
    const goalDef = goals.find((g) => g.id === goal);
    if (goalDef?.directGoal) {
      const saved = await onSelect(
        goalDef.directGoal,
        goalDef.stage,
        PRIMARY_INTENT_BY_GOAL[goal],
      );
      if (saved !== false) resetAfterAnimation();
      return;
    }
    // If the sub-step would only have one option, skip step 2.
    if (goal === "donate" && donationGoal) {
      const option = helpOptions.find((opt) => opt.goal === donationGoal);
      const saved = await onSelect(
        donationGoal,
        option?.stage,
        PRIMARY_INTENT_BY_GOAL[goal],
      );
      if (saved !== false) resetAfterAnimation();
      return;
    }
    setSelectedGoal(goal);
    setStep("stage");
  };

  const handleOptionSelect = async (goal: TreatmentGoalValue) => {
    if (!selectedGoal) return;
    const option = options.find((opt) => opt.goal === goal);
    const saved = await onSelect(
      goal,
      option?.stage,
      PRIMARY_INTENT_BY_GOAL[selectedGoal],
    );
    if (saved !== false) resetAfterAnimation();
  };

  const handleBack = () => {
    setStep("goal");
    setSelectedGoal(null);
  };

  const options =
    selectedGoal === "preserve"
      ? preserveOptions
      : selectedGoal === "donate"
        ? helpOptions
        : growOptions;
  const optionTitle = t("you.intent_picker.title_stage");

  return (
    <Dialog
      visible={visible}
      onClose={handleClose}
      layout="centered"
      closeButton="minimal"
    >
      <DialogHeader
        // The way back sits in the title row, level with the close.
        leading={
          step === "stage" ? (
            <IconButton
              accessibilityLabel={t("you.shared.back")}
              action="you.shared.back"
              onPress={handleBack}
              hitSlop={12}
              size="sm"
              pressedOpacity={1}
            >
              <ChevronLeft
                size={20}
                color={EMBER.colors.textLight}
                strokeWidth={2}
              />
            </IconButton>
          ) : null
        }
        title={
          step === "goal" ? t("you.intent_picker.title_goal") : optionTitle
        }
        subtitle={step === "goal" ? t("you.intent_picker.subtitle") : undefined}
        style={{ marginBottom: 20 }}
      />

      {isSaving && (
        <Text
          className="font-dm-sans"
          style={{
            color: EMBER.colors.textLight,
            fontSize: 13,
            marginBottom: 12,
            textAlign: "center",
          }}
        >
          {t("you.intent_picker.journey_save_in_progress")}
        </Text>
      )}
      {saveError && (
        <ActionBanner
          variant="important"
          message={saveError}
          style={{ marginBottom: 12 }}
        />
      )}

      <ScrollView
        testID="intent-picker-options"
        showsVerticalScrollIndicator={false}
        style={{ maxHeight: 420 }}
        contentContainerStyle={{ gap: 8, paddingBottom: 4 }}
      >
        {step === "goal"
          ? goals.map((goal) => {
              const directGoal =
                goal.id === "donate" && helpOptions.length === 1
                  ? helpOptions[0]?.goal
                  : goal.directGoal;
              const isDirectAndSelected =
                directGoal !== undefined && currentIntent === directGoal;
              return (
                <PickerRow
                  key={goal.id}
                  Icon={goal.Icon}
                  label={goal.label}
                  sublabel={goal.sublabel}
                  selected={isDirectAndSelected}
                  trailing={
                    directGoal !== undefined
                      ? isDirectAndSelected
                        ? "check"
                        : "none"
                      : "chevron"
                  }
                  disabled={isSaving}
                  onPress={() => void handleGoalSelect(goal.id)}
                />
              );
            })
          : options.map((opt, i) => {
              const isSelected = currentIntent === opt.goal;
              return (
                <PickerRow
                  key={`${opt.goal}-${i}`}
                  Icon={opt.Icon}
                  label={opt.label}
                  sublabel={opt.sublabel}
                  selected={isSelected}
                  trailing={isSelected ? "check" : "none"}
                  disabled={isSaving}
                  onPress={() => void handleOptionSelect(opt.goal)}
                />
              );
            })}
      </ScrollView>
    </Dialog>
  );
}
